import React, { useState } from 'react'
import { useApp } from '../context/AppContext'

export function Tournaments() {
  const { state, dispatch } = useApp()
  const [showCreator, setShowCreator] = useState(false)
  const [newTournament, setNewTournament] = useState({
    name: '',
    type: 'single',
  })

  const currentUser = state.users.find(u => u.id === state.currentUserId)
  const myTournaments = state.tournaments.filter(t => t.createdBy === state.currentUserId)

  const handleCreate = (e) => {
    e.preventDefault()
    if (!newTournament.name.trim()) return

    const tournament = {
      id: `tour_${Date.now()}`,
      name: newTournament.name.trim(),
      type: newTournament.type,
      createdBy: state.currentUserId,
      createdAt: Date.now(),
      participants: [],
      matches: [],
      status: 'open',
    }

    dispatch({ type: 'ADD_TOURNAMENT', payload: tournament })
    setNewTournament({ name: '', type: 'single' })
    setShowCreator(false)
  }

  const generateBracket = (tournament) => {
    // Simple bracket generation
    const participants = tournament.participants.length > 0
      ? tournament.participants
      : state.characters.filter(c => c.userId === state.currentUserId).slice(0, 4)

    if (participants.length < 2) return null

    const matches = []
    for (let i = 0; i < participants.length; i += 2) {
      if (participants[i + 1]) {
        matches.push({
          id: `match_${i}`,
          round: 1,
          player1: participants[i],
          player2: participants[i + 1],
          winner: null,
        })
      }
    }

    return matches
  }

  if (!currentUser) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold mb-4">Sign in to Create Tournaments</h2>
        <p className="text-gray-400">Create an account to host tournaments</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tournaments</h1>
          <p className="text-gray-400">Host and join fighting tournaments</p>
        </div>
        <button
          onClick={() => setShowCreator(true)}
          className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all"
        >
          + New Tournament
        </button>
      </div>

      {/* Tournament Creator Modal */}
      {showCreator && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#131a33] border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Create Tournament</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">Tournament Name</label>
                <input
                  type="text"
                  value={newTournament.name}
                  onChange={(e) => setNewTournament({ ...newTournament, name: e.target.value })}
                  className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg text-white focus:outline-none focus:border-cyan-500/50"
                  placeholder="Enter tournament name"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Format</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setNewTournament({ ...newTournament, type: 'single' })}
                    className={`p-3 rounded-lg text-left transition-all ${
                      newTournament.type === 'single'
                        ? 'bg-cyan-500/20 border border-cyan-500/50'
                        : 'bg-black/30 border border-white/10'
                    }`}
                  >
                    <div className="font-medium">Single Elimination</div>
                    <div className="text-xs text-gray-500">Lose once and you're out</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewTournament({ ...newTournament, type: 'double' })}
                    className={`p-3 rounded-lg text-left transition-all ${
                      newTournament.type === 'double'
                        ? 'bg-cyan-500/20 border border-cyan-500/50'
                        : 'bg-black/30 border border-white/10'
                    }`}
                  >
                    <div className="font-medium">Double Elimination</div>
                    <div className="text-xs text-gray-500">Two losses to eliminate</div>
                  </button>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreator(false)}
                  className="flex-1 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tournament List */}
      {myTournaments.length === 0 ? (
        <div className="text-center py-16 bg-white/5 rounded-2xl border border-white/10 border-dashed">
          <div className="text-4xl mb-4">🏆</div>
          <h3 className="text-xl font-bold mb-2">No Tournaments Yet</h3>
          <p className="text-gray-400 mb-4">Create your first tournament to start competing</p>
          <button
            onClick={() => setShowCreator(true)}
            className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg"
          >
            Create Tournament
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {myTournaments.map((tournament) => {
            const matches = generateBracket(tournament)

            return (
              <div key={tournament.id} className="bg-white/5 border border-white/10 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold">{tournament.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs uppercase px-2 py-0.5 bg-white/10 rounded text-gray-400">
                        {tournament.type === 'single' ? 'Single Elimination' : 'Double Elimination'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(tournament.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                    tournament.status === 'open'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-500/20 text-gray-400'
                  }`}>
                    {tournament.status}
                  </span>
                </div>

                {/* Bracket Preview */}
                {matches && matches.length > 0 && (
                  <div className="mt-4 p-4 bg-black/30 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-400 mb-3">Round 1</h4>
                    <div className="space-y-2">
                      {matches.map((match) => (
                        <div key={match.id} className="flex items-center gap-4 text-sm">
                          <div className="flex-1 text-right">{match.player1.name}</div>
                          <div className="text-gray-500">vs</div>
                          <div className="flex-1">{match.player2.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-4">
                  <button className="px-4 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg text-sm font-medium hover:bg-cyan-500/20 transition-all">
                    View Bracket
                  </button>
                  <button className="px-4 py-2 bg-white/5 text-gray-400 rounded-lg text-sm font-medium hover:bg-white/10 transition-all">
                    Invite Players
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
